import { styled } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';

const CIRCLE_SIZE = 96;
const IMAGE_SIZE = 88;

export const Section = styled.div`
  width: 100%;
  padding-top: ${({ theme }) => theme.space[6]};
  position: relative;

  ${({ theme }) => theme.mediaQueries.up({ breakpointAlias: 'sm2' })} {
    padding-top: ${({ theme }) => theme.space[8]};
  }
`;

/**
 * SEO h1 heading — replaces the heading that was previously in JumbotronSection.
 * Visually compact so it doesn't dominate; semantically present for crawlers and screen readers.
 */
export const GalleryHeading = styled.h1`
  font-size: ${pxToRem(24)};
  font-weight: 600;
  letter-spacing: -0.021em;
  line-height: 1.3;
  text-align: center;
  margin: 0 0 ${({ theme }) => theme.space[4]};
  padding: 0 ${({ theme }) => theme.space[6]};
  color: ${({ theme }) => theme.colors.mono14};

  ${({ theme }) => theme.mediaQueries.up({ breakpointAlias: 'sm2' })} {
    font-size: ${pxToRem(32)};
    padding: 0 ${({ theme }) => theme.space[8]};
    margin-bottom: ${({ theme }) => theme.space[5]};
  }
`;

/**
 * Flex wrapper that centers the ScrollContainer when items fit,
 * but lets it fill width (and scroll) when they overflow.
 */
export const ScrollWrapper = styled.div`
  display: flex;
  justify-content: center;
`;

/**
 * inline-flex so it only takes as much width as its children.
 * When items fit → narrower than parent → parent centers it.
 * When items overflow → grows to max-width: 100% → scrolls from left edge.
 */
export const ScrollContainer = styled.div`
  display: inline-flex;
  gap: ${({ theme }) => theme.space[5]};
  overflow-x: auto;
  max-width: 100%;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  padding: ${({ theme }) => theme.space[2]} ${({ theme }) => theme.space[6]};

  &::-webkit-scrollbar {
    display: none;
  }

  ${({ theme }) => theme.mediaQueries.up({ breakpointAlias: 'sm2' })} {
    gap: ${({ theme }) => theme.space[6]};
    padding: ${({ theme }) => theme.space[2]} ${({ theme }) => theme.space[8]};
  }
`;

export const ArrowButton = styled.button<{ $dir: 'left' | 'right' }>`
  display: none;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $dir }) => ($dir === 'left' ? 'left: 4px;' : 'right: 4px;')}
  z-index: 1;
  width: ${pxToRem(36)};
  height: ${pxToRem(36)};
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.12);
  background: white;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.12);
  }

  ${({ theme }) => theme.mediaQueries.up({ breakpointAlias: 'sm2' })} {
    display: flex;
  }
`;

export const StyleItem = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.space[3]};
  flex-shrink: 0;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  outline: none;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.brandBlue6};
    outline-offset: 4px;
    border-radius: ${pxToRem(4)};
  }
`;

/**
 * Outer circle — 96×96, flex-centered, border-radius 50%.
 * The border ring lives here. The inner image is 88×88 so there's a
 * 4px gap between the image edge and the ring — matching the marcom
 * reference (/wedding-invitations/?print=true&category=cultural).
 *
 * Non-active state: subtle #EBEBEB border so circles don't blend into
 * the white background (Jeff's feedback).
 */
export const ThumbnailWrapper = styled.div<{ $isActive: boolean }>`
  width: ${pxToRem(CIRCLE_SIZE)};
  height: ${pxToRem(CIRCLE_SIZE)};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.2s ease;
  border: 2px solid ${({ $isActive }) => ($isActive ? 'rgba(90, 84, 74, 1)' : '#EBEBEB')};

  ${StyleItem}:hover & {
    border-color: ${({ $isActive }) => ($isActive ? 'rgba(90, 84, 74, 1)' : 'rgba(187, 177, 163, 1)')};
  }

  ${StyleItem}:active & {
    border-color: rgba(187, 177, 163, 1);
  }
`;

export const Thumbnail = styled.img`
  width: ${pxToRem(IMAGE_SIZE)};
  height: ${pxToRem(IMAGE_SIZE)};
  border-radius: 50%;
  object-fit: cover;
  display: block;
`;

export const Label = styled.span<{ $isActive: boolean }>`
  font-size: ${pxToRem(12)};
  line-height: ${pxToRem(16)};
  font-weight: ${({ $isActive }) => ($isActive ? 600 : 400)};
  color: ${({ theme }) => theme.colors.mono14};
  white-space: nowrap;
  transition: color 0.15s ease;

  ${({ theme }) => theme.mediaQueries.up({ breakpointAlias: 'sm2' })} {
    font-size: ${pxToRem(13)};
  }
`;
